/**
 * "Tiếp tục với Google" trên facego.site.
 *
 * Chuyển hẳn sang trang của Google (không popup, không nạp script bên thứ ba),
 * xin thẳng `id_token`, rồi Google trả về `/auth/google#id_token=…`. Trang đó
 * gửi token lên `POST /extension/auth/google` — cùng cửa với extension, nên một
 * người đăng nhập Google ở đâu cũng vào đúng một tài khoản.
 *
 * `state` và `nonce` cất trong sessionStorage của tab: chỉ đúng tab vừa bấm nút
 * mới nhận lại được kết quả, và một token bị bắt lại không dùng lại được.
 */

const STORAGE_KEY = "facego_google_sign_in";

export interface PendingGoogleSignIn {
  nonce: string;
  referralCode: string;
  remember: boolean;
  state: string;
}

function randomHex(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function googleRedirectUri(origin: string = window.location.origin): string {
  return `${origin}/auth/google`;
}

export function buildGoogleAuthUrl(clientId: string, pending: PendingGoogleSignIn, redirectUri: string): string {
  const params = new URLSearchParams({
    client_id: clientId,
    nonce: pending.nonce,
    prompt: "select_account",
    redirect_uri: redirectUri,
    response_type: "id_token",
    scope: "openid email profile",
    state: pending.state,
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export function startGoogleSignIn(clientId: string, options: { referralCode?: string; remember?: boolean } = {}): void {
  const pending: PendingGoogleSignIn = {
    nonce: randomHex(),
    referralCode: options.referralCode || "",
    remember: options.remember ?? true,
    state: randomHex(),
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  window.location.assign(buildGoogleAuthUrl(clientId, pending, googleRedirectUri()));
}

export type GoogleCallbackResult =
  | { idToken: string; kind: "ok"; pending: PendingGoogleSignIn }
  | { kind: "cancelled" }
  | { kind: "error"; message: string };

/** Đọc kết quả Google trả về; luôn xoá lượt đang chờ để không dùng lại được. */
export function readGoogleCallback(hash: string = window.location.hash): GoogleCallbackResult {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  if (params.get("error") === "access_denied") return { kind: "cancelled" };
  if (params.get("error")) return { kind: "error", message: "Google từ chối đăng nhập. Thử lại." };
  let pending: PendingGoogleSignIn | null = null;
  try {
    pending = raw ? JSON.parse(raw) as PendingGoogleSignIn : null;
  } catch {
    pending = null;
  }
  // Không có lượt chờ, hoặc `state` lệch: kết quả này không phải do tab này xin.
  if (!pending || params.get("state") !== pending.state) {
    return { kind: "error", message: "Phiên đăng nhập Google không hợp lệ. Thử lại từ đầu." };
  }
  const idToken = params.get("id_token");
  if (!idToken) return { kind: "error", message: "Google không trả về thông tin đăng nhập. Thử lại." };
  return { idToken, kind: "ok", pending };
}
